// The grants screen's two writes — the state half of `<AdminGrantsPanel/>`,
// split out the way `createArtifactActions` and `createMachineRemove` are: a
// mutation with a confirm step, a per-row busy key and a failure notice is
// STATE, not rendering, and it unit-tests without a shell.
//
// Four rules:
//
//   - **Both writes RE-READ, never patch.** Issue answers the created grant and
//     revoke answers the parked one, but the LIST resolves `active` at one
//     server-side instant across every row — a row spliced in from an echo would
//     be the only one on the page evaluated against a different clock.
//   - **`revoked: false` is reported, not swallowed.** It means the row was
//     ALREADY stopped: a re-run, or a second operator racing the first. Nothing
//     visibly changes, so silence here reads as "the click did nothing" — the
//     `gradeWebhookTest` rule, one level down.
//   - **Busy is keyed BY GRANT.** Two rows are two independent calls; a single
//     "which row is working" slot would re-enable row A the moment row B
//     started, which is how an operator double-fires a revoke.
//   - **A refused write renders the API's own sentence.** Only the port knows
//     WHICH of its refusals fired (`no_issuer`, `unknown_subject`,
//     `kind_not_admitted`, `expires_in_past`), so the `detail` is shown
//     verbatim rather than re-worded here.
//   - **Both writes ride an operator credential** (`withOperatorCredential`,
//     `admin:grants`, #5709): no session carries that scope. A door refusal
//     (`not_staff` / `reauth_required`) sends no write and lands in
//     `refusal()` for `<OperatorRefusalAlert/>`, never in the error toast.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { type AdminGrant, type IssueGrantInput, issueGrant, revokeGrant } from './adminGrants';
import { createKeyedMutation } from './keyed-mutation';
import {
  type OperatorRefusal,
  type OperatorSend,
  withOperatorCredential,
} from './operator-credential';

export type GrantActions = {
  /** True while THIS grant has a revoke in flight. */
  readonly isBusy: (id: string) => boolean;
  /** True while an issue is in flight — the form's submit gate. */
  readonly issuing: () => boolean;
  /** Failure copy for the surface's `<Toast/>`, or null. */
  readonly error: () => string | null;
  readonly clearError: () => void;
  /** Benign-outcome copy (an already-stopped grant), or null. */
  readonly notice: () => string | null;
  readonly clearNotice: () => void;
  /** Why the operator-credential door refused the last write, or null. */
  readonly refusal: () => OperatorRefusal | null;
  /**
   * Issue one. Resolves `true` when the grant landed, so the form clears itself
   * on success and keeps a rejected draft for the operator to fix.
   */
  readonly issue: (input: IssueGrantInput) => Promise<boolean>;
  /** The grant awaiting revoke confirmation, or null. Feed to `<ConfirmDialog/>`. */
  readonly pending: () => AdminGrant | null;
  readonly askRevoke: (grant: AdminGrant) => void;
  readonly cancel: () => void;
  /** Run the revoke under confirmation. A second call mid-flight is dropped. */
  readonly confirm: () => void;
};

/**
 * Write state for one grants screen. `token` is passed to every call rather than
 * defaulted here so a test drives it without a session, and `onWritten` is the
 * RE-READ both writes end in.
 */
export function createGrantActions(input: {
  readonly token: string | null;
  readonly onWritten: () => void;
}): GrantActions {
  const [notice, setNotice] = createSignal<string | null>(null);
  const [pending, setPending] = createSignal<AdminGrant | null>(null);
  const [refusal, setRefusal] = createSignal<OperatorRefusal | null>(null);
  const asOperator = <T>(name: string, write: (send: OperatorSend) => Promise<T>) =>
    withOperatorCredential({ scopes: ['admin:grants'], name, token: input.token }, write);
  // ONE keyed mutation for both acts, so both report through ONE error signal —
  // two would have to be synced through a `createEffect`, which is the thing
  // this seam exists to prevent. Issue is the surface's single form and holds
  // the reserved key below; every other key is a `grants.id` uuid, which cannot
  // collide with it. `onStart` clears the benign-outcome notice INSIDE the
  // guard: a confirm the guard dropped must not wipe the notice the call still
  // in flight produced.
  const ISSUE_KEY = 'issue';
  const act = createKeyedMutation({
    fallback: t('admin.grants_revoke_failed'),
    onStart: () => {
      setNotice(null);
      setRefusal(null);
    },
  });

  const issue = async (body: IssueGrantInput): Promise<boolean> => {
    let landed = false;
    await act.run(
      ISSUE_KEY,
      async () => {
        const out = await asOperator('admin console: issue a grant', (c) => issueGrant(body, c));
        if (out.kind !== 'ok') return void setRefusal(out.kind);
        input.onWritten();
        landed = true;
      },
      t('admin.grants_issue_failed'),
    );
    // False for a REFUSED write and for one the guard dropped alike — both mean
    // the draft is not on the server, so the form keeps it either way.
    return landed;
  };

  const confirm = (): void => {
    const grant = pending();
    if (grant === null) return;
    void act.run(grant.id, async () => {
      const out = await asOperator('admin console: revoke a grant', (c) =>
        revokeGrant(grant.id, c),
      );
      // Close only the confirm THIS call opened — nothing stops the operator
      // dismissing it and asking about another row mid-flight.
      if (pending() === grant) setPending(null);
      if (out.kind !== 'ok') return void setRefusal(out.kind);
      if (!out.value.revoked) setNotice(t('admin.grants_already_revoked'));
      input.onWritten();
    });
  };

  return {
    isBusy: act.isBusy,
    issuing: () => act.isBusy(ISSUE_KEY),
    error: act.error,
    clearError: act.clearError,
    notice,
    clearNotice: () => setNotice(null),
    refusal,
    issue,
    pending,
    askRevoke: (grant) => {
      act.clearError();
      setNotice(null);
      setRefusal(null);
      setPending(grant);
    },
    cancel: () => setPending(null),
    confirm,
  };
}
